/**
 * A list's text search, carried in the URL as `q`.
 *
 * Keeping it in the query string is what lets a list hand the project detail screen a
 * complete URL, so Editor -> Project -> Back returns to the list as it actually was —
 * editor filter and search box together — instead of a list that has quietly reset
 * its search.
 *
 * The URL is written from the *debounced* search text, so typing does not rewrite the
 * location on every keystroke.
 */
import { useEffect } from 'react';

export const SEARCH_PARAM = 'q';

/** The search text held in a URLSearchParams, as the search Input expects it. */
export const readSearchParam = (params) => (params ? params.get(SEARCH_PARAM) || '' : '');

/** A new URLSearchParams with the search text set to `value`; '' clears it. */
export const writeSearchParam = (prevParams, value) => {
  const next = new URLSearchParams(prevParams || undefined);
  if (value) next.set(SEARCH_PARAM, value);
  else next.delete(SEARCH_PARAM);
  return next;
};

/**
 * Keep `q` in step with the debounced search text.
 *
 * The equality guard is load-bearing: react-router hands back a fresh `searchParams`
 * object on every render, so an effect that wrote unconditionally would re-trigger
 * itself for ever.
 */
export const useSyncSearchParam = (searchParams, setSearchParams, debouncedSearch) => {
  useEffect(() => {
    if (readSearchParam(searchParams) === (debouncedSearch || '')) return;
    setSearchParams((prev) => writeSearchParam(prev, debouncedSearch), { replace: true });
  }, [searchParams, setSearchParams, debouncedSearch]);
};
